import {
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { HamButtonComponent } from '../../../../../aumico-components/src/lib';
import { INameId } from '@modeso/types__aumico-lib-core-be';
import { ITagDTO } from '@modeso/types__aumico-lib-companies-be';
import { localizationKeys } from '../../constants/localizationKeys';
import { IPersonOverviewRow } from '../../interfaces/person-overview-row.interface';
import { PersonsFilter } from '../../interfaces/persons-filter.interface';
import { TasksUpdate } from '../../enums/tasks-update.enum';

const FLYOUT_WIDTH = 140;
const VIEWPORT_PADDING = 10;

/**
 * REV-22155 — the persons overview (pure view). Built from the same blocks as the companies
 * overview (`companies-list2`): tabs Aktiv / Inaktiv, search, `user-filter-drop-down` filters,
 * `ham-table-with-actions`, Mandatsleiter / Sachbearbeiter flyout per row, `bulk-edit-action-bar`.
 *
 * The page loads, filters and saves; this view gets the rows already filtered and only reports.
 * Display state stays here: checked rows, open flyout, open bulk dialog, search text in the options
 * of a filter dropdown.
 */
@Component({
    selector: 'aumico-persons-overview',
    // e2e selector contract (e2e/fixtures/persons.ts)
    host: { 'data-testid': 'persons-overview' },
    templateUrl: './persons-overview.component.html',
    styleUrls: ['./persons-overview.component.scss'],
})
export class PersonsOverviewComponent implements OnChanges, AfterViewInit {
    @ViewChild('pageWrapper') pageWrapper!: ElementRef;

    /** Already filtered by the page (tab, search, filters). */
    @Input() persons: IPersonOverviewRow[] = [];
    @Input() activeCount = 0;
    @Input() inactiveCount = 0;
    @Input() showInactive = false;
    @Input() filter: PersonsFilter = {};
    /** Users of the organization (`id` + `name`). */
    @Input() users: INameId[] = [];
    @Input() tags: ITagDTO[] = [];
    /** With CPMM a Sachbearbeiter change asks which tasks follow (as in the companies overview). */
    @Input() cpmmActive = false;

    @Output() tabChange = new EventEmitter<boolean>();
    @Output() filterChange = new EventEmitter<PersonsFilter>();
    @Output() personClick = new EventEmitter<string>();
    @Output() newClick = new EventEmitter<void>();
    @Output() exportClick = new EventEmitter<void>();
    @Output() assigneeChange = new EventEmitter<{ personId: string; userId: string }>();
    @Output() clerkChange = new EventEmitter<{ personId: string; userId: string; tasksUpdate: TasksUpdate }>();
    @Output() bulkAssigneeChange = new EventEmitter<{ personIds: string[]; userId: string }>();
    @Output() bulkClerkChange = new EventEmitter<{ personIds: string[]; userId: string; tasksUpdate: TasksUpdate }>();
    /** Button is there; the page reacts once persons have projects (context-Projekte.md). */
    @Output() bulkCreateNextFinancialYear = new EventEmitter<string[]>();
    /** After confirmation; the page sets `active: false` for each of them. */
    @Output() bulkDeactivate = new EventEmitter<string[]>();

    readonly local = localizationKeys;
    readonly columnClasses = ['hamScaleCol', 'hamTextCol200', 'hamTextCol200', 'hamTextCol200', 'hamTextCol200', 'hamTextCol200', 'hamTextCol200'];
    // Like companies: three actions plus "Deaktivieren" as the separate red action.
    readonly bulkActions = [
        { id: 'ASSIGNEE_CHANGE', translationKey: localizationKeys.aumico_persons_overview_bulk_assignee },
        { id: 'CLERK_CHANGE', translationKey: localizationKeys.aumico_persons_overview_bulk_clerk },
        {
            id: 'CREATE_NEXT_FINANCIAL_YEAR',
            translationKey: localizationKeys.aumico_persons_overview_bulk_next_financial_year,
            maxSelections: 100,
            testId: 'persons-bulk-next-year',
        },
    ];
    readonly deactivateAction = {
        id: 'DEACTIVATE_PERSONS',
        translationKey: localizationKeys.aumico_persons_overview_bulk_deactivate,
    };

    readonly searchControl = new FormControl('', { nonNullable: true });
    readonly bulkUserControl = new FormControl<INameId | null>(null);

    selectedIds = new Set<string>();
    openedFlyout: { personId: string; field: 'assignee' | 'clerk' } | null = null;
    flyoutPosition: { top: number; left: number; right: number; isAbove: boolean } = { top: 0, left: 0, right: 0, isAbove: false };
    openBulkDialog: 'assignee' | 'clerk' | null = null;
    /** Sachbearbeiter change waiting for the "which tasks" answer (row or bulk). */
    pendingClerkChange: { personIds: string[]; userId: string; bulk: boolean } | null = null;
    selectedTaskUpdate = TasksUpdate.UPDATE_TASKS_ASSIGNED_TO_CURRENT;
    readonly tasksUpdateOption = TasksUpdate;
    showBulkDeactivateDialog = false;

    /** Options of the filter dropdowns after their own search box. */
    assigneeOptions: INameId[] = [];
    clerkOptions: INameId[] = [];
    tagOptions: INameId[] = [];
    /** Copies for the filter dropdowns: `user-filter-drop-down` changes the array it gets, and a new
     *  array per change detection would reset its selection — so stable copies, renewed with `filter`. */
    assigneeFilterIds: string[] = [];
    clerkFilterIds: string[] = [];
    tagFilterIds: string[] = [];

    ngAfterViewInit(): void {
        setTimeout(() => this.updateTableHeight(), 0);
    }

    @HostListener('window:resize', [])
    onWindowResize(): void {
        this.updateTableHeight();
    }

    /**
     * Same as the companies overview: the table gets the height that is left below it, otherwise the
     * last rows are cut off instead of scrolling.
     */
    private updateTableHeight(): void {
        const element = this.pageWrapper?.nativeElement as HTMLElement | undefined;
        if (!element) return;
        const available = window.innerHeight - element.getBoundingClientRect().top - 76;
        element.style.setProperty('--ham-table-max-h', `${Math.max(200, available - 20)}px`);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['users']) {
            this.assigneeOptions = this.users;
            this.clerkOptions = this.users;
        }
        if (changes['tags']) {
            this.tagOptions = this.tagsAsOptions();
        }
        if (changes['filter']) {
            this.searchControl.setValue(this.filter?.textSearch ?? '', { emitEvent: false });
            this.assigneeFilterIds = [...(this.filter?.mandateManagerIds ?? [])];
            this.clerkFilterIds = [...(this.filter?.clerkIds ?? [])];
            this.tagFilterIds = [...(this.filter?.tagIds ?? [])];
        }
        if (changes['persons']) {
            setTimeout(() => this.updateTableHeight(), 0);
        }
        if (changes['persons'] || changes['showInactive']) {
            // Keep only checked rows that are still shown.
            const shown = new Set(this.persons.map((person) => person._id));
            this.selectedIds = new Set([...this.selectedIds].filter((id) => shown.has(id)));
        }
    }

    get allSelected(): boolean {
        return this.persons.length > 0 && this.persons.every((person) => this.selectedIds.has(person._id));
    }

    // ── tabs, search, filters ──────────────────────────────────────────────────────────────────

    selectTab(showInactive: boolean): void {
        if (showInactive === this.showInactive) return;
        this.selectedIds = new Set();
        this.tabChange.emit(showInactive);
    }

    submitSearch(): void {
        this.filterChange.emit({ ...this.filter, textSearch: this.searchControl.value.trim() || undefined });
    }

    clearSearch(): void {
        this.searchControl.setValue('');
        this.filterChange.emit({ ...this.filter, textSearch: undefined });
    }

    onAssigneeFilter(ids: string[]): void {
        this.filterChange.emit({ ...this.filter, mandateManagerIds: [...ids] });
    }

    onClerkFilter(ids: string[]): void {
        this.filterChange.emit({ ...this.filter, clerkIds: [...ids] });
    }

    onTagFilter(ids: string[]): void {
        this.filterChange.emit({ ...this.filter, tagIds: [...ids] });
    }

    /** Each dropdown searches its own options — no shared list (bug in companies, bugs-firmen.md §1). */
    searchAssigneeOptions(term: string): void {
        this.assigneeOptions = this.matching(this.users, term);
    }

    searchClerkOptions(term: string): void {
        this.clerkOptions = this.matching(this.users, term);
    }

    searchTagOptions(term: string): void {
        this.tagOptions = this.matching(this.tagsAsOptions(), term);
    }

    // ── rows ───────────────────────────────────────────────────────────────────────────────────

    onRowClick(row: IPersonOverviewRow): void {
        this.personClick.emit(row._id);
    }

    toggleRow(id: string, event: MouseEvent): void {
        event.stopPropagation();
        const next = new Set(this.selectedIds);
        if (next.has(id)) {
            next.delete(id);
        } else {
            next.add(id);
        }
        this.selectedIds = next;
    }

    toggleAll(): void {
        this.selectedIds = this.allSelected ? new Set() : new Set(this.persons.map((person) => person._id));
    }

    openUserFlyout(personId: string, field: 'assignee' | 'clerk', button: HamButtonComponent): void {
        if (this.openedFlyout?.personId === personId && this.openedFlyout.field === field) {
            this.closeUserFlyout();
            return;
        }
        const element = button.nativeButton?.nativeElement as HTMLElement | undefined;
        if (!element) return;
        this.flyoutPosition = this.flyoutPositionFor(element.getBoundingClientRect());
        this.openedFlyout = { personId, field };
    }

    closeUserFlyout(): void {
        this.openedFlyout = null;
    }

    chooseUser(row: IPersonOverviewRow, userId: string): void {
        const field = this.openedFlyout?.field;
        this.closeUserFlyout();
        if (field === 'assignee' && userId !== row.assigneeId) {
            this.assigneeChange.emit({ personId: row._id, userId });
        }
        if (field === 'clerk' && userId !== row.clerkId) {
            this.requestClerkChange([row._id], userId, false);
        }
    }

    isFlyoutOpen(personId: string, field: 'assignee' | 'clerk'): boolean {
        return this.openedFlyout?.personId === personId && this.openedFlyout.field === field;
    }

    getInitials(name: string): string {
        const names = (name ?? '').trim().split(/\s+/).filter(Boolean);
        if (names.length === 0) return '';
        return names.length > 1 ? `${names[0][0]}${names[1][0]}`.toUpperCase() : names[0].substring(0, 2).toUpperCase();
    }

    // ── bulk actions ───────────────────────────────────────────────────────────────────────────

    onBulkAction(actionId: string): void {
        if (actionId === 'ASSIGNEE_CHANGE') {
            this.openBulkDialog = 'assignee';
        } else if (actionId === 'CLERK_CHANGE') {
            this.openBulkDialog = 'clerk';
        } else if (actionId === 'DEACTIVATE_PERSONS') {
            this.showBulkDeactivateDialog = true;
        } else if (actionId === 'CREATE_NEXT_FINANCIAL_YEAR') {
            this.bulkCreateNextFinancialYear.emit([...this.selectedIds]);
        } else if (actionId === 'CLOSE_BULK_EDIT') {
            this.selectedIds = new Set();
        }
    }

    confirmBulkUser(): void {
        const user = this.bulkUserControl.value;
        if (!user) return;
        const change = { personIds: [...this.selectedIds], userId: user.id };
        if (this.openBulkDialog === 'assignee') {
            this.bulkAssigneeChange.emit(change);
            this.selectedIds = new Set();
        }
        if (this.openBulkDialog === 'clerk') {
            this.requestClerkChange(change.personIds, change.userId, true);
        }
        this.cancelBulkUser();
    }

    /** "Nur bisherige" / "Alle" — like the companies overview; without CPMM straight to "all". */
    private requestClerkChange(personIds: string[], userId: string, bulk: boolean): void {
        if (!this.cpmmActive) {
            this.emitClerkChange(personIds, userId, bulk, TasksUpdate.UPDATE_ALL_TASKS);
            return;
        }
        this.selectedTaskUpdate = TasksUpdate.UPDATE_TASKS_ASSIGNED_TO_CURRENT;
        this.pendingClerkChange = { personIds, userId, bulk };
    }

    confirmClerkChange(): void {
        const pending = this.pendingClerkChange;
        if (!pending) return;
        this.emitClerkChange(pending.personIds, pending.userId, pending.bulk, this.selectedTaskUpdate);
        this.pendingClerkChange = null;
    }

    cancelClerkChange(): void {
        this.pendingClerkChange = null;
    }

    private emitClerkChange(personIds: string[], userId: string, bulk: boolean, tasksUpdate: TasksUpdate): void {
        if (bulk) {
            this.bulkClerkChange.emit({ personIds, userId, tasksUpdate });
            this.selectedIds = new Set();
        } else {
            this.clerkChange.emit({ personId: personIds[0]!, userId, tasksUpdate });
        }
    }

    confirmBulkDeactivate(confirmed: boolean): void {
        this.showBulkDeactivateDialog = false;
        if (!confirmed) return;
        this.bulkDeactivate.emit([...this.selectedIds]);
        this.selectedIds = new Set();
    }

    cancelBulkUser(): void {
        this.openBulkDialog = null;
        this.bulkUserControl.reset();
    }

    trackById(_: number, row: IPersonOverviewRow): string {
        return row._id;
    }

    private tagsAsOptions(): INameId[] {
        return this.tags.map((tag) => ({ id: tag._id, name: tag.name }));
    }

    private matching(options: INameId[], term: string): INameId[] {
        const wanted = (term ?? '').trim().toLocaleLowerCase();
        return wanted ? options.filter((option) => option.name.toLocaleLowerCase().includes(wanted)) : options;
    }

    /** Below the button if it fits, else above; kept inside the viewport (as in companies-list2). */
    private flyoutPositionFor(rect: DOMRect): { top: number; left: number; right: number; isAbove: boolean } {
        const height = Math.min(this.users.length * 40 + 10, 280);
        const fitsBelow = window.innerHeight - rect.bottom >= height + VIEWPORT_PADDING;
        const top = fitsBelow ? rect.bottom : Math.max(VIEWPORT_PADDING, rect.top - height);
        let left = rect.left;
        if (left + FLYOUT_WIDTH > window.innerWidth - VIEWPORT_PADDING) {
            left = window.innerWidth - FLYOUT_WIDTH - VIEWPORT_PADDING;
        }
        return { top, left, right: rect.right, isAbove: !fitsBelow };
    }
}
